'use client'

import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Key, Loader2 } from '@/components/ui/icons'
import { userFacingErrorMessage } from '@/lib/apiErrors'
import { captureSentryError } from '@/lib/sentry'

import {
  listAccountGenerationApiKeys,
  revokeAccountGenerationApiKey,
  type GenerationApiKeySummary,
} from './apiKeysClient'

/**
 * The signed-in user's account keys: `martini login` approvals made "as you".
 * They belong to no organization, so this personal page is the only place they
 * are listed or revoked; a team sees them only through the member who holds them.
 */

function isExpired(key: { expiresAt?: string | null }): boolean {
  return Boolean(key.expiresAt && new Date(key.expiresAt).getTime() <= Date.now())
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Never'
  return new Date(value).toLocaleString()
}

export function AccountKeysPanel() {
  const [keys, setKeys] = useState<GenerationApiKeySummary[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<GenerationApiKeySummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setKeys(await listAccountGenerationApiKeys())
    } catch (loadError) {
      captureSentryError(loadError, { operation: 'load_account_generation_api_keys', component: 'AccountKeysPanel' })
      setError(userFacingErrorMessage(loadError, 'Account keys could not be loaded. Reload to try again.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => void refresh(), 0)
    return () => window.clearTimeout(timeoutId)
  }, [refresh])

  const revokeKey = async () => {
    if (!revokeTarget) return
    setSubmitting(true)
    setError(null)
    try {
      await revokeAccountGenerationApiKey(revokeTarget.id)
      setKeys(previous =>
        previous.map(key => (key.id === revokeTarget.id ? { ...key, revokedAt: new Date().toISOString() } : key)),
      )
      setRevokeTarget(null)
    } catch (revokeError) {
      captureSentryError(revokeError, {
        operation: 'revoke_account_generation_api_key',
        component: 'AccountKeysPanel',
      })
      setError(userFacingErrorMessage(revokeError, 'The key could not be revoked. Try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="space-y-3">
      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <Key className="size-4 text-muted-foreground" aria-hidden="true" />
              <h2 className="text-sm font-medium">CLI account keys</h2>
            </div>
            <p className="mt-1 max-w-xl text-xs text-muted-foreground">
              Keys from <span className="text-foreground">martini login</span> approved as you. Each acts in every
              organization you belong to and expires 90 days after login.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link href="/docs/cli">CLI Docs</Link>
          </Button>
        </div>

        <div className="border-t border-border">
          {loading ? (
            <div className="flex items-center gap-2 px-5 py-6 text-xs text-muted-foreground">
              <Loader2 className="size-3 animate-spin" aria-hidden="true" />
              Loading keys...
            </div>
          ) : keys.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-xs text-muted-foreground">No CLI account keys</p>
              <p className="mt-1 text-[10px] text-muted-foreground/60">
                Run martini login and approve as you to see a key here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {keys.map(key => (
                <li key={key.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm">{key.deviceLabel ?? key.label}</span>
                      <span className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {key.revokedAt ? 'Revoked' : isExpired(key) ? 'Expired' : 'Active'}
                      </span>
                    </div>
                    <code className="mt-1 block text-[11px] text-muted-foreground">
                      {key.prefix}••••{key.lastFour}
                    </code>
                    <p className="mt-1 text-[10px] text-muted-foreground/60">
                      Last used {formatDate(key.lastUsedAt)}
                      {key.expiresAt && !key.revokedAt
                        ? ` · ${isExpired(key) ? 'Expired' : 'Expires'} ${formatDate(key.expiresAt)}`
                        : ''}
                    </p>
                  </div>
                  {!key.revokedAt ? (
                    <Button variant="outline" size="sm" disabled={submitting} onClick={() => setRevokeTarget(key)}>
                      Revoke
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      <Dialog open={revokeTarget !== null} onOpenChange={open => !open && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Account Key</DialogTitle>
            <DialogDescription>
              The CLI on {revokeTarget?.deviceLabel ?? 'that device'} stops working immediately; run martini login there
              again for a new key.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void revokeKey()} disabled={submitting}>
              {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
              Revoke Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
